/**
 * P5 before the first close: the start position from `TeamState.kpiHistory[0]` (SimStart), labelled as the start. Year-on-year views, the
 * statements and the net interest income bridge wait for the first released round (they need a `RoundReport`).
 */
import { useState } from "react";
import type { ContentCatalog, MarketView, TeamState } from "@bsim/contracts";
import type { KpiId } from "@bsim/domain";
import { fiscalYearLabel, formatKpi } from "../format";
import { firstUseDefinition, kpiLabel } from "../format/labels";
import { usePreviewContext } from "../shell/PreviewProvider";
import { Drawer } from "../theme/Drawer";
import { PeerTable, type PeerRow } from "../theme/PeerTable";
import { GxCard, GxEmptyState } from "../theme/primitives";
import { CREDIT_KPIS, LIQUIDITY_KPIS } from "./kpiTables";
import { MixTables } from "./MixTables";
import { groupedPeerRows, othersOf, peerRows, peersInformative, rateSheetRows } from "./peers";
import { GxNotes } from "../theme/GxNotes";
import { GxTabs } from "../theme/GxTabs";
import "./financials.css";

/** One plain line per view, ahead of the first term it uses. */
const START_PLAIN: Readonly<Record<string, string>> = {
  capital: "How much loss cushion the bank holds, and how much it has already lost on bonds that have fallen in price.",
  earnings: "How much the bank earns on its loans and securities after paying for deposits, and what it keeps.",
  liquidity: "How easily the bank could pay out if depositors withdrew money.",
  credit: "How many loans go bad, and the money set aside to cover that.",
};

const START_TABS = [
  ["capital", "Capital and marks", ["cet1Reported", "cet1Marked", "aoci", "htmMark", "tbvps", "ptbv"]],
  ["earnings", "Earnings", ["nii", "nim", "roa", "rotce", "efficiencyRatio", "costOfDeposits"]],
  ["liquidity", "Liquidity", LIQUIDITY_KPIS],
  ["credit", "Credit and reserves", CREDIT_KPIS],
] as const satisfies readonly (readonly [string, string, readonly KpiId[]])[];

/** The views of the all-banks drawer: the Peers table's groups in the Peers table's order (six groups over four tabs), then the mix of the books and the rate sheets. Six tabs, one row, never two. */
const DRAWER_TABS = [
  ["value", "Value and earnings", ["Prices and value", "Earnings"]],
  ["capital", "Capital and marks", ["Capital"]],
  ["liquidity", "Liquidity", ["Liquidity"]],
  ["credit", "Credit and growth", ["Credit", "Growth"]],
] as const satisfies readonly (readonly [string, string, readonly string[]])[];
const RATES_TAB = "rates";
const MIX_TAB = "mix";

/** "Compare with all banks": every other bank beside you, by view, with the rate sheets last. Neutral names; public measures only. */
function AllBanks({ market, mySeatId, onClose }: { readonly market: MarketView; readonly mySeatId: string; readonly onClose: () => void }) {
  const [tab, setTab] = useState<string>(DRAWER_TABS[0][0]);
  const productNames = new Map((usePreviewContext()?.products ?? []).map((p) => [p.id as string, p.label] as const));
  const names = othersOf(market, mySeatId).map((b) => b.name);
  const groups = DRAWER_TABS.find(([id]) => id === tab)?.[2] ?? [];
  const rows = tab === RATES_TAB ? rateSheetRows(market, mySeatId, productNames) : tab === MIX_TAB ? [] : groupedPeerRows(market, mySeatId, groups);
  const informative = peersInformative(market);
  return (
    <Drawer title="All banks" onClose={onClose} footer="Public measures only: what any investor could read for a bank like these. Limits and plans are never shown.">
      <div className="gx-allbanks" data-testid="chair.allbanks">
        <GxTabs tabs={[...DRAWER_TABS.map(([id, label]) => ({ id, label })), { id: MIX_TAB, label: "Loan and deposit mix" }, { id: RATES_TAB, label: "Rate sheets" }].map((t) => ({ ...t, testId: `chair.allbanks.tabs.${t.id}` }))} active={tab} onSelect={setTab} label="All banks views" testId="chair.allbanks.tabs" />
        {tab === MIX_TAB ? <MixTables market={market} mySeatId={mySeatId} testId="chair.allbanks" />
          : rows.length === 0 ? <GxEmptyState title="Nothing to compare here">No bank has published these yet.</GxEmptyState>
          : <PeerTable rows={rows} testId="chair.allbanks.table" caption={tab === RATES_TAB ? "Rate sheets of all banks" : "Public measures of all banks"} bankNames={names} medianLabel="Median" showPeers phone="grid" firstColumn={tab === RATES_TAB ? "Product" : "Measure"} />}
        <GxNotes notes={[informative ? null : "Every bank starts from the same position, so each row shows the same figure for all six."]} />
      </div>
    </Drawer>
  );
}

/** "Your bank at the start" (chair DESIGN §3.3): the start position by view, open on the page. With a released round it adds the peer median and your rank; before that every bank is level, so it says so and shows You alone. */
export function StartKpis({ team, catalog, market }: { readonly team: TeamState; readonly catalog: ContentCatalog; readonly market: MarketView }) {
  const [tab, setTab] = useState<string>(START_TABS[0][0]);
  const [open, setOpen] = useState(false);
  const start = team.kpiHistory.find((h) => h.roundNo === 0)?.kpis ?? {};
  const [, , ids] = START_TABS.find(([id]) => id === tab) ?? START_TABS[0];
  const label = `${fiscalYearLabel(catalog, 0)} close`;
  // With peers shown, only measures the other banks publish have a row; before that (or while no peer figure is there) every measure has its own, You alone.
  const peers = peersInformative(market) ? peerRows(market, team.team.seatId, ids, start) : [];
  const informative = peers.length > 0;
  const rows: PeerRow[] = (informative ? [...peers] : ids.flatMap((id) => (start[id] === undefined ? [] : [{ id, label: kpiLabel(id), term: id, you: formatKpi(id, start[id] ?? 0), median: null, rank: null, banks: [], level: false }])));
  return (
    <GxCard title="Your bank at the start" aside={<button type="button" className="gx-btn gx-btn--secondary" data-testid="chair.compare" onClick={() => setOpen(true)}>Compare with all banks</button>} className="gx-startpos" testId="chair.start">
      <div className="gx-card-b">
        <p className="gx-small gx-muted">{label}. Peers are the other five banks in this game.</p>
        <GxTabs tabs={START_TABS.map(([id, name]) => ({ id, label: name, testId: `financials.start.tabs.${id}` }))} active={tab} onSelect={setTab} label="Start position views" testId="financials.start.tabs" />
        <p className="gx-small gx-muted gx-fin-plain" data-testid="financials.start.plain">{START_PLAIN[tab]}</p>
        {ids.includes("cet1Marked" as never) && firstUseDefinition("cet1Marked") !== null ? <p className="gx-small gx-muted" data-testid="financials.start.cet1def">Marked CET1: {firstUseDefinition("cet1Marked")}</p> : null}
        {rows.length === 0 ? <GxEmptyState title="No start figures for this view">Nothing was recorded for these measures at the start.</GxEmptyState>
          : <PeerTable rows={rows} testId={`financials.start.${tab}`} caption={`Start position, ${label}`} showPeers={informative} foldLevel={false} phone="grid" />}
        {informative ? null : <p className="gx-startpos-callout" data-testid="chair.start.level">Every bank starts from this position, so there is no gap to show yet. After round 1 you will see where you stand against the other five.</p>}
      </div>
      {open ? <AllBanks market={market} mySeatId={team.team.seatId} onClose={() => setOpen(false)} /> : null}
    </GxCard>
  );
}

export function StartPosition({ team, catalog, market }: { readonly team: TeamState; readonly catalog: ContentCatalog; readonly market: MarketView }) {
  return (
    <div data-testid="financials.start">
      <div className="gx-pagehead">
        <div><div className="gx-eyebrow">Bank</div><h1 className="gx-h1" style={{ marginTop: 4 }}>Bank</h1></div>
      </div>
      <StartKpis team={team} catalog={catalog} market={market} />
      <p className="gx-small gx-muted gx-fin-check">The statements, year-on-year views and the view of what moved net interest income open when your first year closes.</p>
    </div>
  );
}
